import { Component, Input } from '@angular/core';
import moment from 'moment-business-days';
import { IAlbum, Lightbox } from 'ngx-lightbox';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ContactHelper } from 'src/app/helpers/contact-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Asset from 'src/app/model/asset';
import ProjectContact from 'src/app/model/company/projectContact';
import SubProjectContact from 'src/app/model/company/subProjectContact';
import ProjectInvoice from 'src/app/model/invoice/project-invoice';
import ProductAsset from 'src/app/model/product-asset';
import IPBoardArrows from 'src/app/model/product/ip-board/ip-board-arrows';
import IPBoardPlacement from 'src/app/model/product/ip-board/ip-board-placement';
import { Adjustment } from 'src/app/model/project/adjustment';
import { ClientCompletedDetailSubProject } from 'src/app/model/project/client-completed-detail-sub-project';
import { Repo } from 'src/app/model/project/repo';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { OnView } from 'src/app/model/tabs/on-view';
import { OnViewUpdatable } from 'src/app/model/tabs/on-view-updatable/on-view-updatable';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { SubProjectControllerRequestService } from 'src/app/services/api/sub-project-controller-request.service';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { FileHelper } from '../../../../helpers/file-helper';

@Component({
  selector: 'client-adjustment-detail',
  templateUrl: './client-adjustment-detail.component.html',
  styleUrls: ['./client-adjustment-detail.component.scss'],
  providers: [FormatPhoneNumber],
})
export class ClientAdjustmentDetailComponent
  extends SubscriptionBasedComponent
  implements OnView<string>
{
  @Input()
  baseProjectId: number;

  @Input()
  projectId: number;

  @Input()
  subProjectId: number;

  @Input()
  form: boolean = false;

  @Input()
  type: string;

  @Input()
  title: string;

  @Input()
  executed: boolean = false;

  detail: ClientCompletedDetailSubProject;
  executionAssets: ProductAsset[] = [];
  adjustmentExternalAssets: ProductAsset[] = [];

  numInvolvedCompanies = 1;

  loading: boolean = false;

  adjustment: Adjustment | Repo | SoldRented;

  designAssets: Asset[];
  pdfList: Asset[];

  projectHasIPBoards: boolean = false;

  arrows: IPBoardArrows;

  minDate: Date = new Date();

  onViewUpdatable: OnViewUpdatable<string>;

  subProjectContact: SubProjectContact;

  matchingProjectInvoice: ProjectInvoice;
  isImage = FileHelper.isImage;

  constructor(
    private subProjectService: SubProjectControllerRequestService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private clientCompanySelectService: ClientCompanySelectService,
    private _lightbox: Lightbox,
    private formatPhoneNumber: FormatPhoneNumber
  ) {
    super();
  }

  onView(onViewUpdatable?: OnViewUpdatable<string>): void {
    this.onViewUpdatable = onViewUpdatable;

    if (this.detail == null) {
      moment.updateLocale('nl-be', {});
      this.minDate = DateHelper.getMomentBusinessDayMinProjectPlacement().toDate();
      this.getDetail();
    }

    this.addSubscription(
      this.projectDisplayObservableService.getSubProjectDesignVariableChangedSubscribable(),
      () => this.calculateDesignAssets()
    );
  }

  private getDetail(): void {
    this.loading = true;

    this.subProjectService
      .getClientCompletedDetail(this.projectId, this.subProjectId, this.form)
      .then((res) => {
        if (res.oldPlatformId != null && this.onViewUpdatable != null) {
          this.onViewUpdatable.onViewUpdate(res.oldPlatformId.toString());
        }
        this.detail = res;
        const involvedCompanies =
          ProjectHelper.getUniqueCompaniesFromProjectContacts(
            this.detail.projectContacts
          );
        if (involvedCompanies != null) {
          this.numInvolvedCompanies = involvedCompanies.length;
        }
        this.calculateMatchingInvoice();
        this.executionAssets = this.detail.latestExecutionAssets;
        this.detail.products.forEach((p) => {
          const isIPBoard = ProductHelper.isIPBoard(p);
          this.projectHasIPBoards = this.projectHasIPBoards || isIPBoard;
          this.executionAssets = CollectionHelper.isPopulated(
            this.executionAssets
          )
            ? this.executionAssets
            : this.executionAssets.concat(p.productAssets);
          if (this.arrows == null && isIPBoard) {
            const ipBoardPlacement = <IPBoardPlacement>p.productPlacement;
            if (IPBoardHelper.hasArrows(ipBoardPlacement)) {
              this.arrows = ipBoardPlacement.arrows;
            }
          }
        });

        if (this.form) {
          this.setAdjustment();
          this.subProjectContact = res.subProjectContact;
        } else {
          this.adjustment = res.subProject;
          this.subProjectContact = res.subProject.subProjectContact;

          if (
            this.adjustment.projectPlacement != null &&
            this.adjustment.projectPlacement.projectPlacementAssets.length > 0
          ) {
            this.adjustmentExternalAssets =
              this.adjustment.projectPlacement.projectPlacementAssets.filter(
                (a) =>
                  a.formSection === 'adjustmentExternal' &&
                  !a.archived &&
                  a.asset != null &&
                  !FileHelper.isPdf(a.asset)
              );
          }

          this.calculateDesignAssets();
          this.calculatePDFList();
        }
      })
      .finally(() => (this.loading = false));
  }

  setAdjustment() {
    this.adjustment =
      this.type === 'adjustment'
        ? new Adjustment()
        : this.type === 'repo'
        ? new Repo()
        : new SoldRented();
    let address = {
      ...this.detail.address,
    };
    delete address.id;
    delete address.coordinates;
    this.adjustment.projectPlacement.address = address;
    this.adjustment.projectPlacement.placementFloor =
      this.detail.placementFloor;
    this.adjustment.currentCreatedAt = new Date();

    if (this.type === 'repo') (this.adjustment as Repo).repoByInpress = true;
  }

  private calculateDesignAssets(): void {
    let designAssets: Asset[] =
      this.adjustment.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        this.adjustment.projectPlacement.projectPlacementAssets
      )
        ? []
        : this.adjustment.projectPlacement.projectPlacementAssets.map((pa) =>
            pa == null ||
            pa.asset == null ||
            pa.archived ||
            pa.formSection !== 'extraDesign'
              ? null
              : pa.asset
          );
    CollectionHelper.filterInPlace(designAssets, (a) => a != null);

    if (
      designAssets.length === 0 &&
      this.adjustment.type === SUB_PROJECT_TYPES.SOLDRENTED
    ) {
      const soldRented = <SoldRented>this.adjustment;
      if (
        soldRented.signSticker != null &&
        CollectionHelper.isPopulated(soldRented.signSticker.examples)
      ) {
        designAssets = soldRented.signSticker.examples.map((e) =>
          e.archived || e.asset == null ? null : e.asset
        );
        CollectionHelper.filterInPlace(designAssets, (a) => a != null);
      }
    }

    this.designAssets = designAssets;
  }

  private calculatePDFList(): void {
    if (
      this.adjustment.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        this.adjustment.projectPlacement.projectPlacementAssets
      )
    ) {
      this.pdfList = null;
      return;
    }

    const pdfList: Asset[] = [];
    this.adjustment.projectPlacement.projectPlacementAssets.forEach((pa) => {
      if (
        pa.asset != null &&
        !pa.archived &&
        (pa.formSection === 'adjustmentExternal' ||
          pa.formSection === 'extraDesign') &&
        FileHelper.isPdf(pa.asset)
      ) {
        pdfList.push(pa.asset);
      }
    });
    this.pdfList = pdfList;
  }

  private calculateMatchingInvoice(): void {
    if (!CollectionHelper.isPopulated(this.detail.projectInvoices)) {
      return;
    }
    const selectedCompany =
      this.clientCompanySelectService.getSelectedCompany();
    if (selectedCompany == null) {
      return;
    }
    this.matchingProjectInvoice = this.detail.projectInvoices.find(
      (inv) => inv.company != null && inv.company.id === selectedCompany.id
    );
  }

  openLightbox(): void {
    if (!CollectionHelper.isPopulated(this.designAssets)) {
      return;
    }
    const album: IAlbum[] = this.designAssets.map((a) => {
      return {
        src: '/api/assets/' + a.uuid,
        thumb: '/api/assets/' + a.uuid,
        caption: 'Ontwerp',
      };
    });
    this._lightbox.open(album);
  }

  openAdjustmentExternalLightbox(): void {
    if (!CollectionHelper.isPopulated(this.adjustmentExternalAssets)) {
      return;
    }
    const album: IAlbum[] = this.adjustmentExternalAssets.map((a) => {
      return {
        src: '/api/assets/' + a.asset.uuid,
        thumb: '/api/assets/' + a.asset.uuid,
        caption: 'Ontwerp',
      };
    });
    this._lightbox.open(album);
  }

  mail(projectContact: ProjectContact): String {
    ContactHelper.setFormDisplays(projectContact, this.formatPhoneNumber);
    return (
      'mailto:' +
      projectContact.contact.emailDisplay +
      '?Subject=' +
      this.type.charAt(0).toUpperCase() +
      this.type.slice(1) +
      ' voor job #' +
      this.projectId +
      '&body=Beste ' +
      projectContact.contact.firstName +
      ','
    );
  }
}
